import { useUpdateMediaLibFileMutation } from "@api/media-library";
import Spinner from "@components/ui/spinner";
import { MediaLibDialogBase } from "@editor/modules/media-lib";
import { cn } from "@lib/cn";
import { useAppDispatch } from "@store";
import { closeDialog } from "@store/dialogs";
import { toast } from "react-toastify";
import Button from "@/components/ui/button";
import { track } from "@/features/analytics";
import type { IMediaFile } from "@/shared/types";

const DIALOG_ID = "media-lib-archive-file";

interface IProps {
  fileData: IMediaFile;
  onRemoveFile: (id: string) => void;
}

export const MediaLibFileArchiveDialog = ({
  fileData,
  onRemoveFile,
}: IProps) => {
  const dispatch = useAppDispatch();

  const [updateFile, { isLoading }] = useUpdateMediaLibFileMutation();

  const handleClose = () => {
    dispatch(closeDialog(DIALOG_ID));
  };

  const handleSubmit = async () => {
    if (isLoading) return;

    try {
      const updatedFileData = await updateFile({
        id: fileData._id,
        data: {
          archived: true,
          folderId: null,
        },
      }).unwrap();
      onRemoveFile(updatedFileData._id);
    } catch (e) {
      track("error archiving media file", {
        fileData,
        error: e,
      });
      toast.error("Error archiving file");
    }

    handleClose();
  };

  return (
    <MediaLibDialogBase>
      <MediaLibDialogBase.Header title="Archive file" onClose={handleClose} />
      <MediaLibDialogBase.Content>
        <p className="tw-text-[13px]">
          Are you sure that you want to archive file{" "}
          <b className="tw-font-semibold">“{fileData.title}”</b>?
        </p>
      </MediaLibDialogBase.Content>
      <MediaLibDialogBase.Footer>
        <Button variant="secondary" className="tw-mr-4" onClick={handleClose}>
          Go back
        </Button>
        <Button
          variant="destructive"
          onClick={handleSubmit}
          className={cn(isLoading && "tw-cursor-not-allowed")}
        >
          {isLoading ? (
            <>
              <Spinner className="tw-mr-2 tw-size-3 tw-border-white" />
              Archiving...
            </>
          ) : (
            "Yes, archive"
          )}
        </Button>
      </MediaLibDialogBase.Footer>
    </MediaLibDialogBase>
  );
};

MediaLibFileArchiveDialog.dialogId = DIALOG_ID;
